import {IResourceComponentsProps, useShow, useTranslate, useDelete, useNotification} from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {DateField, EmailField, List, EditButton} from "@refinedev/mui";
import {Grid, Paper, Stack, Typography, Alert, Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import React, { useEffect, useState } from "react";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {getDuration} from "../../utils/getDuration";
import {AddressCell} from "../../components/table/AdressCell";
import { useAuth } from "../../contexts/auth-context";
import { canAccessUserData } from "../../utils/role-permissions";
import { Trash2, Shield, CheckCircle, XCircle, HelpCircle, ArrowLeft } from "lucide-react";
import axiosInstance from "../../api/axios-config";
import { getVigilaneStatus, VigilaneStatus } from "../../api/vigilane";


export const UserShow: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const {queryResult} = useShow();
    const {data, isLoading, error} = queryResult;
    const { mutate: deleteUser } = useDelete();
    const { open } = useNotification();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [unlinkCompanyDialogOpen, setUnlinkCompanyDialogOpen] = useState(false);
    const [vigilaneStatus, setVigilaneStatus] = useState<VigilaneStatus | null>(null);
    const [loadingVigilane, setLoadingVigilane] = useState(false);

        const record = data?.data;

    // Load Vigilane status if user has Vigilane
    useEffect(() => {
        const loadVigilaneStatus = async () => {
            if ((record?.hasVigilane || record?.userMonitoring?.isActive) && record?.id) {
                setLoadingVigilane(true);
                try {
                    const status = await getVigilaneStatus(Number(record.id));
                    setVigilaneStatus(status);
                } catch (error) {
                    console.error('Error loading Vigilane status:', error);
                } finally {
                    setLoadingVigilane(false);
                }
            }
        };

        loadVigilaneStatus();
    }, [record?.hasVigilane, record?.userMonitoring?.isActive, record?.id]);

    // Define columns first to avoid conditional hook usage
    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: "createdAt",
                flex: 1,
                headerName: translate("alert-history.fields.createdAt"),
                minWidth: 250,
                renderCell: function render({value}) {
                    return <DateField value={value}/>;
                },
            },
            {
                field: "Adresse",
                flex: 1,
                headerName: translate("alert-history.fields.address"),
                type: "string",
                minWidth: 250,
                renderCell: function render(value) {
                    return <AddressCell latitude={value.row.latitude} longitude={value.row.longitude} />;
                }
            },
            {
                field: "duration",
                flex: 1,
                headerName: translate("alert-history.fields.duration"),
                minWidth: 100,
                renderCell: function render({row}) {
                    return <p>{getDuration({
                        createdAt: row?.createdAt,
                        endedAt: row?.endedAt,
                    })}</p>;
                }
            }
        ],
        [translate],
    );

        // Redirect to /profile if viewing own profile
    useEffect(() => {
        if (!isLoading && record && currentUser && record.id === currentUser.id) {
            navigate('/profile', { replace: true });
        }
    }, [record, currentUser, isLoading, navigate]);

    // Company-based access control
    useEffect(() => {
        if (!isLoading && record && currentUser) {
            // Skip if viewing own profile (will be redirected above)
            if (record.id === currentUser.id) {
                return;
            }

            // Special handling for VIGILANE role
            if (currentUser.role === 'VIGILANE') {
                // VIGILANE can only access users with vigilaneId
                if (!record.vigilaneId) {
                    console.warn(`🚫 Access denied: VIGILANE user ${currentUser.id} tried to access user ${record.id} without vigilaneId`);
                    navigate('/users', {
                        replace: true
                    });
                    return;
                }
            } else {
                // For other roles: check company-based access
                if (!canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
                    console.warn(`🚫 Access denied: User ${currentUser.id} tried to access user ${record.id} from different company`);
                    navigate('/users', {
                        replace: true
                    });
                    return;
                }
            }
        }
    }, [record, currentUser, isLoading, navigate]);

        // Don't render if access should be denied
    if (!isLoading && record && currentUser) {
        if (currentUser.role === 'VIGILANE') {
            // VIGILANE can only access users with vigilaneId
            if (!record.vigilaneId) {
                return null; // Component will unmount due to navigation
            }
        } else {
            // For other roles: check company-based access
            if (!canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
                return null; // Component will unmount due to navigation
            }
        }
    }

    // Show loading or error states
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography>{translate("common.loading")}</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {translate("errors.loadUser", "Failed to load user information")}
            </Alert>
        );
    }

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (record?.id) {
            // For VIGILANE role: only unlink from Vigilane, don't delete user
            if (isVigilane) {
                try {
                    await axiosInstance.patch(`/users/${record.id}`, {
                        vigilaneId: null,
                        hasVigilane: false,
                    });

                    open?.({
                        type: "success",
                        message: translate("user.unlink.success"),
                    });

                    setDeleteDialogOpen(false);
                    navigate('/users');
                } catch (error: any) {
                    open?.({
                        type: "error",
                        message: translate("user.delete.error"),
                        description: error?.response?.data?.message || "Une erreur est survenue",
                    });
                }
            } else {
                // For other roles: delete user completely
                deleteUser(
                    {
                        resource: "users",
                        id: record.id,
                    },
                    {
                        onSuccess: () => {
                            setDeleteDialogOpen(false);
                            navigate('/users');
                        },
                    }
                );
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    const handleUnlinkCompanyClick = () => {
        setUnlinkCompanyDialogOpen(true);
    };

    const handleUnlinkCompanyConfirm = async () => {
        if (record?.id) {
            try {
                await axiosInstance.patch(`/users/${record.id}`, {
                    entrepriseId: null,
                });

                open?.({
                    type: "success",
                    message: translate("user.unlinkCompany.success", "User successfully unlinked from company"),
                });

                setUnlinkCompanyDialogOpen(false);
                navigate('/users');
            } catch (error: any) {
                open?.({
                    type: "error",
                    message: translate("user.unlinkCompany.error", "Failed to unlink user from company"),
                    description: error?.response?.data?.message || "Une erreur est survenue",
                });
            }
        }
    };

    const handleUnlinkCompanyCancel = () => {
        setUnlinkCompanyDialogOpen(false);
    };

    // Check if current user has permission to delete/edit
    // MODERATOR and MODERATOR_FREE can only unlink users from their company, not edit or delete
    // SAFEE_STAFF can edit but not delete
    const isModerator = currentUser?.role === "MODERATOR" || currentUser?.role === "MODERATOR_FREE";
    const canDelete = currentUser?.role && ["ADMIN", "VIGILANE"].includes(currentUser.role);
    const canEdit = currentUser?.role && ["ADMIN", "SAFEE_STAFF", "OPERATOR", "VIGILANE", "OT"].includes(currentUser.role);
    const canUnlinkFromCompany = isModerator && record?.entrepriseId === currentUser?.entrepriseId;
    const isVigilane = currentUser?.role === "VIGILANE";

    return (
        <>
        <Box sx={{ mb: 2 }}>
            <Button
                variant="text"
                startIcon={<ArrowLeft size={20} />}
                onClick={() => navigate(-1)}
                sx={{ color: 'text.secondary' }}
            >
                {translate("common.back", "Back")}
            </Button>
        </Box>
        <Grid container spacing={2}>
            <Grid item xs={12} lg={3}>
                <Paper sx={{p: 2, paddingX: {xs: 4, md: 2}}}>
                    <Stack alignItems="center" spacing={1}>
                        <Avatar
                            src={record?.profilePicture}
                            sx={{width: 120, height: 120}}
                        />
                        <Typography variant="h6">
                            {record?.fullname}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {canEdit && (
                                <EditButton
                                    resource="users"
                                    recordItemId={record?.id}
                                    size="small"
                                />
                            )}
                            {canDelete && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<Trash2 size={16} />}
                                    onClick={handleDeleteClick}
                                >
                                    {translate(isVigilane ? "user.unlink.button" : "user.delete.button")}
                                </Button>
                            )}
                            {canUnlinkFromCompany && record?.entrepriseId && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={handleUnlinkCompanyClick}
                                >
                                    {translate("user.unlinkCompany.button", "Unlink from Company")}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                    <br/>
                    <Stack spacing={1}>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.phone")}
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                            {record?.phone ?? translate("user.fields.phone.notProvided", "Not provided")}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.email")}
                        </Typography>
                        <EmailField value={record?.email}/>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.role")}
                        </Typography>
                        <Typography variant="body1" sx={{
                            textTransform: 'capitalize',
                            color: record?.role === 'ADMIN' ? 'error.main' :
                                   record?.role === 'MODERATOR' ? 'warning.main' :
                                   'text.primary'
                        }}>
                            {record?.role ?? translate("user.fields.role.notDefined", "Not defined")}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.company")}
                        </Typography>
                        <Typography variant="body1">
                            {record?.entreprise?.nom ?? translate("user.fields.company.notAssigned", "Not assigned to any company")}
                        </Typography>

                        {/* Section Vigilane / Monitoring */}
                        {(record?.hasVigilane || record?.userMonitoring) && (
                            <>
                                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Shield size={20} color="#5B21B6" />
                                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#5B21B6' }}>
                                            {translate("user.fields.vigilane.title", "Vigilane")}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" fontWeight="bold">
                                        {translate("user.fields.vigilane.tlsId", "TLS ID")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {record?.vigilaneId ?? "N/A"}
                                    </Typography>

                                    <Typography variant="body2" fontWeight="bold">
                                        {translate("user.fields.externalId", "External ID")}
                                    </Typography>
                                    {record?.userMonitoring?.externalId ? (
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {record.userMonitoring.externalId}
                                        </Typography>
                                    ) : (
                                        <Chip
                                            label={translate("user.fields.noExternalId", "No External ID")}
                                            color="error"
                                            size="small"
                                        />
                                    )}

                                    <Typography variant="body2" fontWeight="bold">
                                        {translate("user.fields.vigilane.status", "Connection Status")}
                                    </Typography>
                                    {loadingVigilane ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {translate("common.loading", "Loading...")}
                                        </Typography>
                                    ) : vigilaneStatus ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {vigilaneStatus.connectionStatus === 'connected' ? (
                                                <CheckCircle size={16} color="#22c55e" />
                                            ) : vigilaneStatus.connectionStatus === 'disconnected' ? (
                                                <XCircle size={16} color="#ef4444" />
                                            ) : (
                                                <HelpCircle size={16} color="#9ca3af" />
                                            )}
                                            <Typography variant="body2" sx={{
                                                color: vigilaneStatus.connectionStatus === 'connected' ? '#22c55e' :
                                                       vigilaneStatus.connectionStatus === 'disconnected' ? '#ef4444' :
                                                       '#9ca3af'
                                            }}>
                                                {vigilaneStatus.connectionStatus === 'connected' ?
                                                    translate("user.fields.vigilane.connected", "Connected") :
                                                 vigilaneStatus.connectionStatus === 'disconnected' ?
                                                    translate("user.fields.vigilane.disconnected", "Disconnected") :
                                                    translate("user.fields.vigilane.unknown", "Unknown")}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="error">
                                            {translate("user.fields.vigilane.error", "Error loading status")}
                                        </Typography>
                                    )}

                                    {vigilaneStatus?.responseTime && (
                                        <>
                                            <Typography variant="body2" fontWeight="bold">
                                                {translate("user.fields.vigilane.responseTime", "Response Time")}
                                            </Typography>
                                            <Typography variant="body2">
                                                {vigilaneStatus.responseTime}ms
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Grid>
            <Grid item xs={12} lg={9}>
                <List
                    headerProps={{
                        title: translate("alert-history.title"),
                    }}
                    canCreate={false}
                >
                    {record?.emergencyHistoryProprietary && <DataGrid
                        columns={columns}
                        rows={record?.emergencyHistoryProprietary}
                        autoHeight
                        initialState={{
                            pagination: {
                                paginationModel: {page: 0, pageSize: 5},
                            },
                        }}
                        pageSizeOptions={[5, 10, 20, 100]}
                        pagination
                    />}
                </List>
            </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
        >
            <DialogTitle id="delete-dialog-title">
                {translate(isVigilane ? "user.unlink.confirm.title" : "user.delete.confirm.title")}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="delete-dialog-description">
                    {translate(isVigilane ? "user.unlink.confirm.message" : "user.delete.confirm.message")}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} color="primary">
                    {translate("user.delete.confirm.cancel")}
                </Button>
                <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                    {translate(isVigilane ? "user.unlink.confirm.confirm" : "user.delete.confirm.confirm")}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Unlink from Company Confirmation Dialog */}
        <Dialog
            open={unlinkCompanyDialogOpen}
            onClose={handleUnlinkCompanyCancel}
            aria-labelledby="unlink-company-dialog-title"
            aria-describedby="unlink-company-dialog-description"
        >
            <DialogTitle id="unlink-company-dialog-title">
                {translate("user.unlinkCompany.confirm.title", "Unlink User from Company")}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="unlink-company-dialog-description">
                    {translate("user.unlinkCompany.confirm.message", "Are you sure you want to unlink this user from the company? The user will no longer be associated with your company but their account will remain active.")}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleUnlinkCompanyCancel} color="primary">
                    {translate("user.unlinkCompany.confirm.cancel", "Cancel")}
                </Button>
                <Button onClick={handleUnlinkCompanyConfirm} color="warning" variant="contained" autoFocus>
                    {translate("user.unlinkCompany.confirm.confirm", "Unlink")}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};
